import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatearMoneda } from "../../config/moneda";
import { PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";
import { useStoreHome } from "../Home/useStoreHome";
import { useStoreCajas } from "../Cajas/useStoreCajas";
import { useStorePos } from "./useStorePos";

const nombreLinea = (linea, t) => linea.TipoLinea === "Libre"
  ? linea.DescripcionManual || t("pos.freeItemLabel")
  : linea.Mercaderia?.sDescripcion || linea.Mercaderia_Descripcion || t("pos.product");

const tipoLinea = (linea, t) => {
  if (linea.bCatalogado) return t("pos.cataloged");
  if (linea.EstadoStock === "Parcial") return t("pos.partialStockStatus");
  if (linea.EstadoStock === "SinStock") return t("pos.outOfStock");
  if (linea.EstadoStock === "Completo") return t("pos.fullStock");
  return linea.TipoLinea === "Libre" ? t("pos.free") : t("pos.stockNotTracked");
};

const claseTipo = (linea) => {
  if (linea.bCatalogado) return "stock-ok";
  if (linea.EstadoStock === "Parcial") return "stock-parcial";
  if (linea.EstadoStock === "SinStock") return "sin-stock";
  if (linea.EstadoStock === "Completo") return "stock-ok";
  return "libre";
};

const cantidad = (valor, locale) => Number(valor || 0).toLocaleString(locale, {
  maximumFractionDigits: 3,
});

const formatearFecha = (valor, locale, t) => {
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return t("pos.dateUnavailable");
  return fecha.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PosVentasRecientes = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-AR";
  const puedeCatalogar = puedeAcceder(PERMISOS.MERCADERIAS_ADMINISTRAR);
  const EmpresaId = useStoreHome((state) => state.EmpresaId);
  const cajaSesionId = useStoreCajas((state) => state.cajaSesionId);
  const ventasRecientes = useStorePos((state) => state.VentasRecientes);
  const cargando = useStorePos((state) => state.CargandoVentasRecientes);
  const error = useStorePos((state) => state.ErrorVentasRecientes);
  const cargarVentasRecientes = useStorePos((state) => state.cargarVentasRecientes);
  const pagosPorVenta = useStorePos((state) => state.PagosPorVenta);
  const clavePrimeraVenta = ventasRecientes[0]?.VentaId || ventasRecientes[0]?.dFecha || null;
  const [ventaAbierta, setVentaAbierta] = useState(clavePrimeraVenta);
  useEffect(() => {
    if (!EmpresaId) return;
    void cargarVentasRecientes({ CajaSesionId: cajaSesionId, Cantidad: 10, forzar: true });
  }, [EmpresaId, cajaSesionId, cargarVentasRecientes]);
  useEffect(() => {
    const venta = ventasRecientes.find((item, indice) => (item.VentaId || item.dFecha || indice) === ventaAbierta);
    if (venta?.VentaId) void useStorePos.getState().cargarPagosVenta(venta.VentaId);
  }, [ventaAbierta, ventasRecientes]);
  const abrirCatalogoLinea = (linea, detalleId, indice) => {
    useStorePos.getState().abrirCatalogarLinea({ linea, detalleId, indice });
  };

  return (
    <section className="pos-pendientes pos-ventas-recientes-panel">
      <div className="pos-pendientes-header">
        <div>
          <h3 className="ing-subtitulo">{t("pos.latestSales")}</h3>
          <p className="pos-ayuda">{t("pos.recentSalesHelp")}</p>
        </div>
        {ventasRecientes.length > 0 && (
          <span className="pos-ticket-badge">{t("pos.saleCount", { count: ventasRecientes.length })}</span>
        )}
      </div>

      {cargando && ventasRecientes.length === 0 ? (
        <div className="pos-ticket-empty" role="status">
          <i className="pi pi-spin pi-spinner" />
          <strong>{t("pos.loadingRecentSales")}</strong>
          <span>{t("pos.queryingSavedOperations")}</span>
        </div>
      ) : error && ventasRecientes.length === 0 ? (
        <div className="pos-ticket-empty" role="alert">
          <i className="pi pi-exclamation-triangle" />
          <strong>{t("pos.recentSalesLoadFailed")}</strong>
          <span>{error}</span>
          <button
            type="button"
            className="pos-action-btn"
            onClick={() => void cargarVentasRecientes({ CajaSesionId: cajaSesionId, Cantidad: 10, forzar: true })}
          >
            {t("pos.retry")}
          </button>
        </div>
      ) : ventasRecientes.length === 0 ? (
        <div className="pos-ticket-empty">
          <i className="pi pi-history" />
          <strong>{t("pos.noSessionSales")}</strong>
          <span>{t("pos.noSessionSalesHelp")}</span>
        </div>
      ) : (
        <div className="pos-ventas-recientes-lista">
          {ventasRecientes.map((venta, ventaIndice) => {
            const lineas = venta.lstDetalle || [];
            const total = venta.oResumen?.TotalConIva ?? venta.oResumen?.Total ?? 0;
            const cantidadLineas = venta.CantidadLineas ?? lineas.length;
            const claveVenta = venta.VentaId || venta.dFecha || ventaIndice;

            return (
              <details
                className="pos-venta-reciente"
                key={claveVenta}
                open={ventaAbierta === claveVenta}
              >
                <summary
                  className="pos-venta-reciente-summary"
                  onClick={(evento) => {
                    evento.preventDefault();
                    const abrir = ventaAbierta !== claveVenta;
                    setVentaAbierta(abrir ? claveVenta : null);
                    if (abrir && venta.VentaId) void useStorePos.getState().cargarPagosVenta(venta.VentaId);
                  }}
                >
                  <span className="pos-venta-reciente-icono">
                    <i className="pi pi-receipt" aria-hidden="true" />
                  </span>
                  <span className="pos-venta-reciente-identidad">
                    <strong>{venta.VentaId ? t("pos.saleHash", { number: venta.VentaId }) : t("pos.confirmedSale")}</strong>
                    <small>
                      {formatearFecha(venta.dFecha, locale, t)} · {venta.ClienteDescripcion || t("pos.finalConsumer")}
                    </small>
                  </span>
                  <span className="pos-venta-reciente-pago">
                    {venta.FormaPago || t("pos.noPaymentMethod")}
                  </span>
                  <span className="pos-venta-reciente-total">{formatearMoneda(total)}</span>
                  <i className="pi pi-chevron-down pos-venta-reciente-chevron" aria-hidden="true" />
                </summary>

                <div className="pos-venta-reciente-detalle">
                  <div className="pos-venta-reciente-metas">
                    <span>{t("pos.lineCount", { count: cantidadLineas })}</span>
                    {venta.DesdeBaseDatos ? (
                      <>
                        <span>{t("pos.units")} <strong>{cantidad(venta.Unidades, locale)}</strong></span>
                        <span>{t("cash.operator")} <strong>{venta.OperadorDescripcion || `#${venta.Operador_Id}`}</strong></span>
                      </>
                    ) : (
                      <>
                        <span>{t("pos.received")} <strong>{formatearMoneda(venta.MontoRecibido)}</strong></span>
                        <span>{t("pos.change")} <strong>{formatearMoneda(venta.Vuelto)}</strong></span>
                      </>
                    )}
                  </div>

                  {venta.VentaId > 0 && (() => {
                    const estadoPagos = pagosPorVenta[venta.VentaId] || {};
                    if (estadoPagos.cargando) return <div className="pos-pagos-persistidos-estado"><i className="pi pi-spin pi-spinner" /> {t("pos.loadingPayments")}</div>;
                    if (estadoPagos.error) return <div className="pos-pagos-persistidos-estado error" role="alert">{t("pos.paymentsLoadFailed", { error: estadoPagos.error })}</div>;
                    if (!estadoPagos.cargado) return null;
                    if (!estadoPagos.lista.length) return <div className="pos-pagos-persistidos-estado">{t("pos.noPaymentDetails")}</div>;
                    return (
                      <section className="pos-pagos-persistidos" aria-label={t("pos.salePayments", { number: venta.VentaId })}>
                        <h4>{t("pos.payments")}</h4>
                        <div className="pos-pagos-persistidos-lista">
                          {estadoPagos.lista.map((pago, indicePago) => (
                            <div key={`${pago.FormaPagoId}-${indicePago}`}>
                              <span><strong>{pago.FormaPago || t("pos.paymentMethod")}</strong><small>{pago.Referencia || ""}</small></span>
                              <span>{formatearMoneda(pago.Importe)}</span>
                              {(Number(pago.MontoRecibido) !== 0 || Number(pago.Vuelto) !== 0) && (
                                <small>{t("pos.received")} {formatearMoneda(pago.MontoRecibido)} · {t("pos.change")} {formatearMoneda(pago.Vuelto)}</small>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="pos-pagos-persistidos-total">{t("pos.totalPayments")} <strong>{formatearMoneda(estadoPagos.total)}</strong></div>
                      </section>
                    );
                  })()}

                  {lineas.length > 0 && (
                    <div className="pos-ticket-table-wrap pos-venta-reciente-tabla">
                      <table className="pos-ticket-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t("pos.product")}</th>
                            <th>{t("pos.type")}</th>
                            <th className="num">{t("common.subtotal")}</th>
                            <th aria-label={t("common.actions")} />
                          </tr>
                        </thead>
                        <tbody>
                          {lineas.map((linea, indice) => (
                            <tr key={venta.DetalleIds?.[indice] || indice}>
                              <td>{indice + 1}</td>
                              <td>
                                <div className="pos-ticket-producto">
                                  <strong>{nombreLinea(linea, t)}</strong>
                                  {(linea.Mercaderia?.sCodigo || linea.Mercaderia_Codigo) && (
                                    <span>
                                      {t("products.codeLabel", { code: linea.Mercaderia?.sCodigo || linea.Mercaderia_Codigo })}
                                    </span>
                                  )}
                                  {linea.EstadoStock === "Parcial" && (
                                    <span>
                                      {t("pos.discountedAndMissing", { discounted: cantidad(linea.CantidadStockConsumida, locale), missing: cantidad(linea.CantidadSinStock, locale) })}
                                    </span>
                                  )}
                                  {linea.EstadoStock === "SinStock" && (
                                    <span>{t("pos.soldWithoutStock", { quantity: cantidad(linea.CantidadSinStock, locale) })}</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className={`pos-ticket-line-tag ${claseTipo(linea)}`}>
                                  {tipoLinea(linea, t)}
                                </span>
                              </td>
                              <td className="num subtotal">{formatearMoneda(linea.Subtotal)}</td>
                              <td className="pos-ticket-actions">
                                {puedeCatalogar && linea.TipoLinea === "Libre" &&
                                  venta.DetalleIds?.[indice] > 0 &&
                                  !linea.bCatalogado && (
                                    <button
                                      type="button"
                                      className="pos-icon-btn"
                                      onClick={() => abrirCatalogoLinea(
                                        linea,
                                        venta.DetalleIds[indice],
                                        indice,
                                      )}
                                      title={t("products.catalogProduct")}
                                    >
                                      <i className="pi pi-book" aria-hidden="true" />
                                    </button>
                                  )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PosVentasRecientes;
